import { formatDate, formatDayOfYear, normalizeToLocalNoon } from '@/lib/format-utils';
import {
  ConditionOperatorEnumType,
  DateUnit,
  RelativeDateDirection,
  type ConditionOperator,
  type RelativeDate,
} from '@reach/shared-types/conditions';
import type { BuilderConditionValue } from '../segment-builder.types';
import { isBetweenDate, isRelativeDate } from './date-value-utils';

function resolveDateValue(value: Date | RelativeDate | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return normalizeToLocalNoon(value);

  const resolved = normalizeToLocalNoon(new Date());
  const valueInDays = getRelativeDateValueInDays(value);
  const dayOffset = value.direction === RelativeDateDirection.AFTER ? valueInDays : valueInDays * -1;
  resolved.setDate(resolved.getDate() + dayOffset);
  return resolved;
}

function assertNeverDateUnit(unit: never): never {
  throw new Error(`Unsupported relative date unit: ${unit}`);
}

function getRelativeDateValueInDays(value: RelativeDate): number {
  switch (value.unit) {
    case DateUnit.DAYS:
      return value.value;
    default:
      return assertNeverDateUnit(value.unit);
  }
}

function formatResolvedDate(date: Date, includeYear: boolean): string {
  return includeYear ? formatDate(date) : formatDayOfYear(date);
}

const SINGLE_DATE_PREVIEW_CONFIG: Partial<
  Record<ConditionOperator, { prefix: string; includeYear: boolean; suffix?: string }>
> = {
  [ConditionOperatorEnumType.EQUALS_MONTH_DAY]: {
    prefix: 'Matches records on:',
    includeYear: false,
    suffix: ' (any year)',
  },
  [ConditionOperatorEnumType.EQUALS_MONTH_DAY_YEAR]: {
    prefix: 'Matches records on:',
    includeYear: true,
  },
  [ConditionOperatorEnumType.GREATER_THAN]: {
    prefix: 'Matches records after:',
    includeYear: true,
  },
  [ConditionOperatorEnumType.LESS_THAN]: {
    prefix: 'Matches records before:',
    includeYear: true,
  },
};

export function getResolvedDatePreviewText(
  operator: ConditionOperator,
  value: BuilderConditionValue | undefined,
): string | null {
  if (!value) return null;

  const singleDatePreview = SINGLE_DATE_PREVIEW_CONFIG[operator];
  if (singleDatePreview) {
    const resolvedDate = resolveDateValue(
      value instanceof Date || isRelativeDate(value) ? value : undefined,
    );
    if (!resolvedDate) return null;

    return `${singleDatePreview.prefix} ${formatResolvedDate(resolvedDate, singleDatePreview.includeYear)}${singleDatePreview.suffix ?? ''}`;
  }

  switch (operator) {
    case ConditionOperatorEnumType.BETWEEN_MONTH_DAY:
    case ConditionOperatorEnumType.BETWEEN_MONTH_DAY_YEAR: {
      if (!isBetweenDate(value)) return null;

      const startDate = resolveDateValue(value.startDate);
      const endDate = resolveDateValue(value.endDate);

      if (!startDate || !endDate) return null;

      const includeYear = operator === ConditionOperatorEnumType.BETWEEN_MONTH_DAY_YEAR;
      const startLabel = formatResolvedDate(startDate, includeYear);
      const endLabel = formatResolvedDate(endDate, includeYear);

      if (startLabel === endLabel) {
        return includeYear
          ? `Matches records on: ${startLabel}`
          : `Matches records on: ${startLabel} (any year)`;
      }

      return includeYear
        ? `Matches records from ${startLabel} - ${endLabel}`
        : `Matches records from ${startLabel} - ${endLabel} (any year)`;
    }
    default:
      return null;
  }
}
